/**
 * Helper : détecte si une ville a une page "grande ville" dédiée disponible,
 * et retourne le path réel d'une ville PE via son slug + code.
 *
 * Cas d'usage :
 *   - AD : mini-threads construisent `/voyance-{slug}-{code}` qui matche
 *     directement les paths V1/V2 de GRANDES_VILLES_VOYANT.
 *   - PE : les paths dans GRANDES_VILLES_ENERG ne suivent pas un pattern
 *     unique (V1 legacy : `/praticiens-en-energetique-*`,
 *     `/professionnels-soins-energetiques-*`, etc. ; V2 : `/praticiens-
 *     energetiques-{slug}-{code}`). On expose donc `getEnergVillePath()`
 *     pour retrouver le path réel via `{slugify(name)}-{code}`.
 */
import { GRANDES_VILLES_VOYANT, GRANDES_VILLES_ENERG } from "./grandesVillesData";

const clean = (p) => (p || "").replace(/\?.*$/, "").replace(/#.*$/, "").toLowerCase();

// Retire accents + ç, apostrophes, caractères spéciaux → slug ASCII propre
const stripAccents = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const slugifyName = (name) =>
  stripAccents(name || "")
    .toLowerCase()
    .replace(/['’]/g, "-")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const AVAILABLE_VOYANT_PATHS = new Set(
  (GRANDES_VILLES_VOYANT || [])
    .filter((v) => v && v.available && v.path)
    .map((v) => clean(v.path))
);

const AVAILABLE_ENERG_PATHS = new Set(
  (GRANDES_VILLES_ENERG || [])
    .filter((v) => v && v.available && v.path)
    .map((v) => clean(v.path))
);

// Map lookup : `${slug}-${code}` → path réel de la grande ville PE
const ENERG_PATH_BY_KEY = new Map();
(GRANDES_VILLES_ENERG || []).forEach((v) => {
  if (!v || !v.available || !v.path || !v.name || v.code === undefined) return;
  const key = `${slugifyName(v.name)}-${String(v.code).toLowerCase()}`;
  ENERG_PATH_BY_KEY.set(key, v.path);
});

/**
 * @param {string} path - Ex: "/voyance-saint-malo-35?from=…" ou "/praticiens-energetiques-lyon-69"
 */
export const isVilleAvailable = (path) => {
  const c = clean(path);
  if (!c) return false;
  return AVAILABLE_ENERG_PATHS.has(c) || AVAILABLE_VOYANT_PATHS.has(c);
};

/**
 * Retourne le path réel d'une grande ville PE si trouvée dans
 * GRANDES_VILLES_ENERG (available: true). Sinon, retourne un path synthétique
 * qui déclenchera l'ouverture de la modale "Bientôt" (via isVilleAvailable()).
 *
 * @param {string} slug - slug du mini-thread (ex: "paris", "val-d-isere")
 * @param {string|number} code - code département (ex: "75", "74")
 */
export const getEnergVillePath = (slug, code) => {
  const normalizedSlug = slugifyName(slug || "");
  const codeLc = String(code || "").toLowerCase();
  const key = `${normalizedSlug}-${codeLc}`;
  if (ENERG_PATH_BY_KEY.has(key)) return ENERG_PATH_BY_KEY.get(key);
  // Pas trouvé → path synthétique (modale s'ouvrira)
  return `/bien-etre-energetique-${slug}-${codeLc}`;
};

export default isVilleAvailable;
